import React, { useContext, useEffect, useState, useCallback } from 'react';
import window from 'global/window';
import * as Cookies from 'js-cookie';
import { isEqual } from 'lodash';
import { PageContext } from '../../../contexts/ContextCreator';
import { getCardDimensions, reshapeMatrix } from './components/helpers';
import 'slick-carousel/slick/slick-theme.scss';
import 'slick-carousel/slick/slick.scss';
import { EmitMetric } from '../../Analytics/VisibilityPixel/VisibilityPixel';
import { REGWALL_ADVICE_HUB } from '../../../contexts/Includes/pageContextHelpers';

const AdvicePageContext = React.createContext(undefined);

const AdvicePageProvider = ({
    children,
    contents,
    topicSelected,
    slideSelected = 1,
    pagination = null,
}) => {
    const [windowWidth, setWindowWidth] = useState(window?.innerWidth);
    const [isSm, setIsSm] = useState(
        window?.innerWidth < 905 && window?.innerWidth >= 600
    );
    const [isXs, setIsXs] = useState(window?.innerWidth < 600);

    const [impressionMetricMap, setImpressionMetricMap] = useState({});

    const windowResize = useCallback(() => {
        setWindowWidth(window?.innerWidth);
    }, []);

    useEffect(() => {
        windowResize();
        window.addEventListener('resize', windowResize, { passive: true });
        return () => {
            window.removeEventListener('resize', windowResize);
        };
    }, [windowResize]);

    const [cardDimensions, setCardDimensions] = useState(
        getCardDimensions(window?.innerWidth)
    );

    useEffect(() => {
        setIsSm(windowWidth < 905);
        setIsXs(windowWidth < 600);
        const _cardDimensions = getCardDimensions(windowWidth);

        if (_cardDimensions?.width !== cardDimensions?.width) {
            setCardDimensions(_cardDimensions);
        }
    }, [windowWidth, cardDimensions]);

    const [communityDiscussion] = useState(
        contents
            ?.filter((c) => c?.type === 'community-discussion')
            .sort((a, b) => (a?.topic?.sort > b?.topic?.sort ? 1 : -1))
    );
    const [editorialContent] = useState(
        contents
            ?.filter((c) => c?.type === 'editorial-content')
            .sort((a, b) => (a?.topic?.sort > b?.topic?.sort ? 1 : -1))
    );

    const [currentTopic] = useState(topicSelected);

    const getVisibleContent = () =>
        currentTopic
            ? contents || []
            : (contents || []).filter((c) => !c?.topic?.featured);

    const [exploreTopics, setExploreTopics] = useState(
        reshapeMatrix(getVisibleContent(), window?.innerWidth)
    );

    const initializeCurrentSlide = () => {
        if (
            Number.isInteger(slideSelected) &&
            Math.ceil(exploreTopics.length / 6) >= slideSelected
        ) {
            return Math.abs(slideSelected);
        }
        if (window && window.location && window.history) {
            const url = new URL(window.location);
            const p = Number.parseInt(url.searchParams.get('page'), 10);
            if (p === slideSelected || Number.isNaN(p)) {
                window.history.replaceState(
                    {},
                    '',
                    `/advice${currentTopic ? `/${currentTopic}` : ''}?page=1`
                );
            }
        }
        return 1;
    };
    const [currentSlide, updateCurrentSlide] = useState(
        initializeCurrentSlide()
    );

    useEffect(() => {
        const reshaped = reshapeMatrix(getVisibleContent(), windowWidth);
        if (!isEqual(reshaped, exploreTopics)) {
            setExploreTopics(reshaped);
        }
    }, [contents, windowWidth, exploreTopics, currentTopic]);

    const pageContext = useContext(PageContext);
    const { user } = pageContext;

    const onTopicSelect = ({ topic }) => {
        if (!topic || topic === currentTopic) {
            window.location.assign('/advice');
            return;
        }
        window.location.assign(`/advice/${topic}?page=1`);
    };

    const onFireImpression = ({ hash }) => {
        const _metrics = impressionMetricMap;
        _metrics[hash] = true;
        setImpressionMetricMap(_metrics);
    };

    const onDiscussionLinkClick = ({ event, hash, contentUrl }) => {
        EmitMetric({
            click: 1,
            community_content_key: hash,
            // TODO: if we have sponsored company, add company ID
            company_id: [null],
        });
        event.preventDefault();
        Cookies.set('EVENT-LOGIN-TRIGGER', 'advice-hub-registration', {
            expires: 1095,
        });
        let url = contentUrl;
        if (!user) {
            url += `?regwall=${REGWALL_ADVICE_HUB}`;
        }
        window.open(url, '_self');
    };

    return (
        <AdvicePageContext.Provider
            value={{
                communityDiscussion,
                editorialContent,
                content: exploreTopics,
                isLoggedIn: !!user,
                onTopicSelect,
                currentTopic,
                cardDimensions,
                isSm,
                windowWidth,
                updateCurrentSlide,
                currentSlide,
                isXs,
                impressionMetricMap,
                onFireImpression,
                onDiscussionLinkClick,
                pagination,
            }}
        >
            {children}
        </AdvicePageContext.Provider>
    );
};

const useAdvicePage = () => {
    const context = useContext(AdvicePageContext);
    if (context === undefined) {
        throw new Error(
            'useAdvicePage can only be used inside AdvicePageProvider'
        );
    }
    return context;
};

export { AdvicePageProvider, useAdvicePage };
